/**
 * Mobile Bottom Navigation — renders the same 5-trunk site tree as the sidebar (lib/navTree.js).
 * Bar: Home · My Project · Group · Rights · More (Buying Smart, Learn, account, developers).
 */
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Home, User, Menu, Moon, Sun, LogOut, Lock, ChevronRight, Crown, Building, CheckCircle, Eye, X } from 'lucide-react';
import { NAV_TREE, trunkOf, visibleLimbs, DEFAULT_PROJECT_ID, trunkLabel } from '@/lib/navTree';

const API = process.env.REACT_APP_BACKEND_URL;

const THEME = {
  primary: { bg: 'bg-primary', light: 'bg-primary/10', text: 'text-primary' },
  green: { bg: 'bg-green-600', light: 'bg-green-50 dark:bg-green-900/20', text: 'text-green-700 dark:text-green-300' },
  amber: { bg: 'bg-amber-500', light: 'bg-amber-50 dark:bg-amber-900/20', text: 'text-amber-700 dark:text-amber-300' },
  blue: { bg: 'bg-blue-500', light: 'bg-blue-50 dark:bg-blue-900/20', text: 'text-blue-700 dark:text-blue-300' },
  slate: { bg: 'bg-slate-600', light: 'bg-slate-100 dark:bg-slate-800', text: 'text-slate-700 dark:text-slate-300' },
};
const BAR_TRUNKS = ['project', 'group', 'rights'];
const SHORT = { project: 'Project', group: 'Group', rights: 'Rights' };
const byId = Object.fromEntries(NAV_TREE.map((t) => [t.id, t]));

// Menu panel that opens ABOVE the bottom bar, so the bar always stays tappable.
const BarPanel = ({ open, onClose, barHeight, testid, title, children }) => {
  useEffect(() => {
    if (!open) return undefined;
    const esc = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="lg:hidden">
      <div className="fixed inset-x-0 top-0 z-40 bg-black/50 animate-in fade-in-0" style={{ bottom: barHeight }} onClick={onClose} data-testid={`${testid}-backdrop`} />
      <div role="dialog" aria-modal="false" style={{ bottom: barHeight }} data-testid={testid}
        className="fixed inset-x-0 z-40 bg-background rounded-t-2xl border-t shadow-2xl max-h-[70vh] overflow-y-auto overscroll-contain p-5 pb-4 animate-in slide-in-from-bottom-4 fade-in-0 duration-200">
        <button type="button" onClick={onClose} aria-label="Close menu" className="absolute right-3 top-3 p-1.5 rounded-full text-muted-foreground hover:bg-muted" data-testid={`${testid}-close`}>
          <X className="h-4 w-4" />
        </button>
        {title}
        {children}
      </div>
    </div>
  );
};

const SheetItem = ({ icon: Icon, label, description, active, onClick, badge, locked, theme, testid }) => (
  <button
    className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${active ? theme?.light || 'bg-primary/10' : 'hover:bg-muted'} ${locked ? 'opacity-70' : ''}`}
    onClick={onClick} data-testid={testid}
  >
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${active ? `${theme?.bg || 'bg-primary'} text-white` : 'bg-muted'}`}>
      <Icon className="h-5 w-5" />
    </div>
    <div className="flex-1 text-left">
      <p className={`font-medium text-sm ${active ? theme?.text || 'text-primary' : ''}`}>{label}</p>
      {description && <p className="text-xs text-muted-foreground">{description}</p>}
    </div>
    {badge && <Badge variant="secondary" className="text-[10px]">{badge}</Badge>}
    {locked ? <Lock className="h-4 w-4 text-muted-foreground" /> : !badge && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
  </button>
);

export default function MobileBottomNav({
  activeSection, onSectionChange, user, selectedProjectId, onOpenAuth, onScrollToTop, darkMode, onToggleDarkMode, onLogout,
}) {
  const [moreOpen, setMoreOpen] = useState(false);
  const [isPostOc, setIsPostOc] = useState(false);
  const navRef = useRef(null);
  const [barHeight, setBarHeight] = useState(64);

  useEffect(() => {
    const pid = selectedProjectId || user?.project_id || DEFAULT_PROJECT_ID;
    axios.get(`${API}/api/projects/${pid}/lifecycle`)
      .then((r) => setIsPostOc(r.data?.stage === 'post_oc')).catch(() => setIsPostOc(false));
  }, [selectedProjectId, user?.project_id]);

  useEffect(() => {
    const el = navRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => setBarHeight(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const isVerifiedBuyer = user?.role === 'admin' || user?.verified_projects?.length > 0 ||
    user?.project_verifications?.some?.((v) => v.status === 'verified');
  const activeTrunk = trunkOf(activeSection);

  const closeAll = () => setMoreOpen(false);
  const go = (section) => { onSectionChange(section); closeAll(); };
  // Every bar button opens its module's main page.
  const tapTrunk = (id) => go(byId[id].landing);
  const tapMore = () => setMoreOpen((o) => !o);

  const TrunkLimbs = ({ trunk }) => {
    const theme = THEME[trunk.color];
    if (trunk.gated && !isVerifiedBuyer) {
      return (<>
        <SheetItem icon={Eye} label="See what members get" theme={theme} description="A sample of the group, and what is free"
          onClick={() => go(trunk.landing)} testid={`m-preview-${trunk.id}`} />
        <SheetItem icon={Lock} label={user ? 'Verify ownership to join' : 'Sign in to join'} locked theme={theme}
          description="Your buyers' group is for verified flat owners"
          onClick={() => (user ? go('verify-ownership') : (closeAll(), onOpenAuth('login')))} testid={`m-locked-${trunk.id}`} />
      </>);
    }
    return visibleLimbs(trunk, { isPostOc }).map((l) => (
      <SheetItem key={l.id} icon={l.icon} label={l.label} description={l.hint} theme={theme}
        active={activeSection === l.id} onClick={() => go(l.id)} testid={`m-limb-${l.id}`}
        badge={undefined} />
    ));
  };

  const BarButton = ({ id, icon: Icon, label, active, onClick, theme }) => (
    <button className="flex flex-col items-center justify-center py-1 px-2 min-w-[56px] touch-manipulation" onClick={onClick} data-testid={`m-bar-${id}`}>
      <div className={`p-2 rounded-full transition-colors ${active ? `${theme?.bg || 'bg-primary'} text-white` : 'text-muted-foreground'}`}>
        <Icon className="h-5 w-5" />
      </div>
      <span className={`text-[10px] mt-0.5 ${active ? `${theme?.text || 'text-primary'} font-medium` : 'text-muted-foreground'}`}>{label}</span>
    </button>
  );

  return (
    <>
      <nav ref={navRef} className="fixed bottom-0 left-0 right-0 z-50 lg:hidden bg-background border-t shadow-lg safe-area-bottom" data-testid="mobile-bottom-nav">
        <div className="flex items-center justify-around px-1 py-2">
          <BarButton id="home" icon={Home} label="Home" active={activeSection === 'home'} onClick={() => { onScrollToTop(); closeAll(); }} />
          {BAR_TRUNKS.map((id) => (
            <BarButton key={id} id={id} icon={byId[id].icon} label={SHORT[id]} theme={THEME[byId[id].color]}
              active={activeTrunk === id} onClick={() => tapTrunk(id)} />
          ))}
          <BarButton id="more" icon={Menu} label="More" active={moreOpen || ['buying', 'learn'].includes(activeTrunk)} onClick={tapMore} />
        </div>
      </nav>

      {/* More: the two remaining trunks + account + developers */}
      <BarPanel open={moreOpen} onClose={() => setMoreOpen(false)} barHeight={barHeight} testid="m-sheet-more"
        title={<p className="font-semibold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>More</p>}>
        <div className="space-y-1">
          {['buying', 'learn'].map((id) => (
            <div key={id} className="pb-2">
              <button type="button" onClick={() => go(byId[id].landing)} data-testid={`m-more-landing-${id}`}
                className={`w-full flex items-center justify-between text-xs font-semibold uppercase tracking-wide px-1 mt-2 mb-1 ${THEME[byId[id].color].text}`}>
                {trunkLabel(byId[id], user)}<span className="normal-case font-normal tracking-normal flex items-center">Main page<ChevronRight className="h-3 w-3" /></span>
              </button>
              <TrunkLimbs trunk={byId[id]} />
            </div>
          ))}

          <Separator className="my-3" />
          <p className="text-xs text-muted-foreground px-1 mb-2">Account</p>
          {user ? (
            <>
              <SheetItem icon={User} label="My Profile" description="Profile and settings" active={activeSection === 'me'} onClick={() => go('me')} testid="m-more-profile" />
              <SheetItem icon={Crown} label="Membership" description="FBC Project Intelligence" active={activeSection === 'subscription'} onClick={() => go('subscription')} testid="m-more-membership" />
              {!isVerifiedBuyer && (
                <SheetItem icon={CheckCircle} label="Verify Ownership" description="Join your buyers' group" active={activeSection === 'verify-ownership'} onClick={() => go('verify-ownership')} testid="m-more-verify" />
              )}
              {user.role === 'admin' && (
                <SheetItem icon={Crown} label="Admin Panel" description="Platform management" active={activeSection === 'admin'} onClick={() => go('admin')} testid="m-more-admin" />
              )}
            </>
          ) : null}
          {user && (
            <SheetItem icon={Building} label="For Developers" description={user?.role === 'developer' ? 'Your developer console' : 'Register as a developer'}
              active={activeSection?.startsWith('developer')} onClick={() => go(user?.role === 'developer' ? 'developer-console' : 'developer-register')} testid="m-more-developers" />
          )}
          <SheetItem icon={darkMode ? Sun : Moon} label={darkMode ? 'Light Mode' : 'Dark Mode'} description="Toggle appearance"
            onClick={() => { onToggleDarkMode(); closeAll(); }} testid="m-more-theme" />
          {user ? (
            <button className="w-full flex items-center gap-3 p-3 rounded-lg text-destructive hover:bg-destructive/10" onClick={() => { onLogout(); closeAll(); }} data-testid="m-more-logout">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-destructive/10"><LogOut className="h-5 w-5" /></div>
              <p className="font-medium text-sm">Logout</p>
            </button>
          ) : (
            <Button className="w-full mt-4" onClick={() => { onOpenAuth('login'); closeAll(); }} data-testid="m-more-signin">Sign In</Button>
          )}
        </div>
      </BarPanel>
    </>
  );
}
